import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Gift, Plus, Loader2, Sparkles, Trophy, TrendingUp, Award, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LOYALTY_TRIGGERS, REWARD_KINDS } from "@/lib/constants";

const TID = {
  ruleAdd: "loyalty-rule-add",
  rewardAdd: "loyalty-reward-add",
  adjustAdd: "loyalty-adjust-add",
  redeemAdd: "loyalty-redeem-add",
};

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-6 rounded-tenant border-border animate-fade-up">
      <div className="flex items-start justify-between mb-5">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-tenant" strokeWidth={1.5} />
      </div>
      <div className="font-display font-semibold tracking-tight text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

export default function TenantLoyalty() {
  const { slug } = useParams();
  const [stats, setStats] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Aliases: "rules" (sidebar) ↔ "dashboard" (interno).
  const urlTab = searchParams.get("tab");
  const allowed = ["rules", "rewards", "transactions", "redeem"];
  const tab = urlTab === "rewards" ? "rewards"
    : urlTab === "transactions" ? "transactions"
    : urlTab === "redeem" ? "redeem"
    : "dashboard"; // rules / null / qualquer outro → dashboard
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v === "dashboard" ? "rules" : v);
    setSearchParams(next, { replace: true });
  };
  void allowed; // reservado para futura validação

  const load = async () => {
    try { const { data } = await api.get("/tenant/loyalty/stats"); setStats(data); }
    catch (e) { /* tolerate */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Diferencial competitivo
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Programa de Fidelidade</h1>
          <p className="text-sm text-muted-foreground mt-1">Pontos, regras e recompensas — sem cashback.</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Tile icon={TrendingUp} label="Pontos emitidos" value={stats.points_earned.toLocaleString("pt-BR")} sub="acumulados" />
          <Tile icon={Trophy} label="Pontos resgatados" value={stats.points_redeemed.toLocaleString("pt-BR")} sub="recompensas trocadas" />
          <Tile icon={Award} label="Membros ativos" value={stats.active_members} sub="com saldo positivo" />
          <Tile icon={Gift} label="Recompensas ativas" value={stats.active_rewards} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Regras</TabsTrigger>
          <TabsTrigger value="rewards">Recompensas</TabsTrigger>
          <TabsTrigger value="transactions">Transações</TabsTrigger>
          <TabsTrigger value="redeem">Resgatar</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><Rules onChanged={load} /></TabsContent>
        <TabsContent value="rewards" className="mt-6"><Rewards onChanged={load} /></TabsContent>
        <TabsContent value="transactions" className="mt-6"><Transactions onChanged={load} /></TabsContent>
        <TabsContent value="redeem" className="mt-6"><Redeem onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Rules({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", trigger: "appointment.completed", points: 10, is_active: true });
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/tenant/loyalty/rules"); setItems(data.items || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/tenant/loyalty/rules", { ...form, points: Number(form.points) });
      toast.success("Regra criada");
      setOpen(false);
      setForm({ name: "", description: "", trigger: "appointment.completed", points: 10, is_active: true });
      load(); onChanged?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir regra?")) return;
    await api.delete(`/tenant/loyalty/rules/${id}`); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">{items.length} regras configuradas</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TID.ruleAdd} className="gap-2"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <Plus className="w-4 h-4" /> Nova regra
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Nova regra de pontuação</DialogTitle>
              <DialogDescription>Se o evento ocorrer, então conceder X pontos.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Gatilho (SE)</Label>
                  <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LOYALTY_TRIGGERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Pontos (ENTÃO)</Label><Input required type="number" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar regra
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        items.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground rounded-tenant">Sem regras. Crie a primeira — ex: "+10 pontos por atendimento concluído".</Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((r) => (
              <Card key={r.id} className="p-5 rounded-tenant border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-lg">{r.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <Badge variant="outline">Se {(LOYALTY_TRIGGERS.find(t => t.v === r.trigger) || {}).l || r.trigger}</Badge>
                      <Badge style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                        Então {r.points >= 0 ? "+" : ""}{r.points} pts
                      </Badge>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
    </div>
  );
}

function Rewards({ onChanged }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", cost_points: 100, kind: "freebie", stock: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/tenant/loyalty/rewards"); setItems(data.items || []); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, cost_points: Number(form.cost_points) };
      if (form.stock === "" || form.stock === null) delete payload.stock; else payload.stock = Number(form.stock);
      await api.post("/tenant/loyalty/rewards", payload);
      toast.success("Recompensa criada");
      setOpen(false);
      setForm({ name: "", description: "", cost_points: 100, kind: "freebie", stock: "" });
      load(); onChanged?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir recompensa?")) return;
    await api.delete(`/tenant/loyalty/rewards/${id}`); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">{items.length} recompensas</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TID.rewardAdd} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <Plus className="w-4 h-4" /> Nova recompensa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Nova recompensa</DialogTitle>
              <DialogDescription>O que o cliente pode trocar pelos pontos.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Custo (pts)</Label><Input required type="number" min={1} value={form.cost_points} onChange={(e) => setForm({ ...form, cost_points: e.target.value })} /></div>
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{REWARD_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Estoque</Label><Input type="number" min={0} placeholder="ilimitado" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground rounded-tenant">Sem recompensas ainda.</Card>
      ) : (
        <div className="grid md:grid-cols-3 gap-3">
          {items.map((r) => (
            <Card key={r.id} className="p-5 rounded-tenant border-border">
              <div className="flex items-start justify-between mb-2">
                <Badge variant="outline">{(REWARD_KINDS.find(k => k.v === r.kind) || {}).l}</Badge>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="font-display font-semibold text-lg">{r.name}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-tenant font-display font-bold text-xl flex items-center gap-1"><Gift className="w-4 h-4" /> {r.cost_points}</div>
                <div className="text-xs text-muted-foreground">{r.stock == null ? "estoque ilimitado" : `${r.stock} em estoque`}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Transactions() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/tenant/loyalty/transactions"); setItems(data.items || []); })(); }, []);
  return (
    <Card className="rounded-tenant border-border overflow-hidden">
      {items.length === 0 ? <div className="p-12 text-center text-muted-foreground">Nenhuma transação ainda.</div> :
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-5 py-3 font-medium">Quando</th><th className="text-left px-5 py-3 font-medium">Tipo</th><th className="text-left px-5 py-3 font-medium">Motivo</th><th className="text-right px-5 py-3 font-medium">Pontos</th></tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} className="border-t border-border">
                <td className="px-5 py-2.5 text-xs whitespace-nowrap">{new Date(t.created_at).toLocaleString("pt-BR")}</td>
                <td className="px-5 py-2.5"><Badge variant="outline">{t.type}</Badge></td>
                <td className="px-5 py-2.5">{t.reason}</td>
                <td className={`px-5 py-2.5 text-right font-mono font-semibold ${t.points >= 0 ? "text-emerald-600" : "text-red-600"}`}>{t.points >= 0 ? "+" : ""}{t.points}</td>
              </tr>
            ))}
          </tbody>
        </table>}
    </Card>
  );
}

function Redeem({ onChanged }) {
  const [clients, setClients] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [client_id, setClientId] = useState("");
  const [reward_id, setRewardId] = useState("");
  const [busy, setBusy] = useState(false);
  // Pedidos do portal (item 3)
  const [portalRequests, setPortalRequests] = useState([]);
  const [requestsBusy, setRequestsBusy] = useState(null);

  const loadPortalRequests = async () => {
    try {
      const r = await api.get("/tenant/loyalty/redemption-requests", {
        params: { status: "pending" },
      });
      setPortalRequests(r.data.items || []);
    } catch (_e) { /* bloco é opcional — silencia */ }
  };

  useEffect(() => {
    (async () => {
      const [c, r] = await Promise.all([
        api.get("/tenant/clients", { params: { limit: 500 } }),
        api.get("/tenant/loyalty/rewards", { params: { only_active: true } }),
      ]);
      setClients(c.data.items || []);
      setRewards(r.data.items || []);
      await loadPortalRequests();
    })();
  }, []);

  const selectedClient = clients.find((c) => c.id === client_id);
  const selectedReward = rewards.find((r) => r.id === reward_id);
  const insufficient = selectedClient && selectedReward && (selectedClient.points_balance || 0) < selectedReward.cost_points;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/tenant/loyalty/redeem", { client_id, reward_id });
      toast.success("Resgate registrado");
      // refresh
      const c = await api.get("/tenant/clients", { params: { limit: 500 } }); setClients(c.data.items || []);
      const r = await api.get("/tenant/loyalty/rewards", { params: { only_active: true } }); setRewards(r.data.items || []);
      onChanged?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const approveRequest = async (req) => {
    setRequestsBusy(req.id);
    try {
      await api.post(`/tenant/loyalty/redemption-requests/${req.id}/approve`);
      toast.success(`Resgate de ${req.client_name} entregue`);
      await loadPortalRequests();
      // Atualiza clientes/recompensas (saldos e estoque mudaram)
      const c = await api.get("/tenant/clients", { params: { limit: 500 } }); setClients(c.data.items || []);
      const r = await api.get("/tenant/loyalty/rewards", { params: { only_active: true } }); setRewards(r.data.items || []);
      onChanged?.();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setRequestsBusy(null); }
  };
  const rejectRequest = async (req) => {
    if (!window.confirm(`Recusar o resgate de ${req.client_name} (${req.reward_name})?`)) return;
    setRequestsBusy(req.id);
    try {
      await api.post(`/tenant/loyalty/redemption-requests/${req.id}/reject`);
      toast.success("Pedido recusado");
      await loadPortalRequests();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setRequestsBusy(null); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {portalRequests.length > 0 && (
        <section
          data-testid="loyalty-portal-requests"
          className="rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-4 space-y-3"
        >
          <div>
            <div className="font-display font-semibold text-sm">Pedidos do portal</div>
            <div className="text-xs text-muted-foreground">
              {portalRequests.length} resgate(s) aguardando entrega.
            </div>
          </div>
          <div className="space-y-2">
            {portalRequests.map((r) => (
              <div
                key={r.id}
                data-testid={`loyalty-portal-request-${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-card border border-border px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {r.client_name} <span className="text-muted-foreground">·</span>{" "}
                    {r.reward_name}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.code} · {r.created_at ? new Date(r.created_at).toLocaleString("pt-BR") : "—"} ·{" "}
                    {r.points_cost} pts
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={requestsBusy === r.id}
                    data-testid={`loyalty-portal-reject-${r.id}`}
                    onClick={() => rejectRequest(r)}
                  >
                    Recusar
                  </Button>
                  <Button
                    size="sm"
                    disabled={requestsBusy === r.id}
                    data-testid={`loyalty-portal-approve-${r.id}`}
                    onClick={() => approveRequest(r)}
                    style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}
                  >
                    Entregar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Card className="p-6 rounded-tenant border-border">
        <div className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-tenant" /> Registrar resgate
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Select value={client_id} onValueChange={setClientId}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · saldo {c.points_balance || 0}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Recompensa</Label>
            <Select value={reward_id} onValueChange={setRewardId}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>{rewards.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.cost_points} pts</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {insufficient && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">Pontos insuficientes para este resgate.</div>
          )}
          <Button type="submit" disabled={busy || !client_id || !reward_id || insufficient} className="gap-2"
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar resgate
          </Button>
        </form>
      </Card>
    </div>
  );
}
